import { IFlow, INode, ITypeNode } from '../Context/FormBuilderContext';

export interface FormBuilderState {
  form: {
    id: string;
    name: string;
    flows: IFlow[];
    status: 'draft' | 'published';
    activeFlows: string[];
    publishType: 'internal' | 'external';
    newFormNodesPublished: INode[];
    newFormNodes: INode[];
    updatedAt: string;
    formStyle: {
      backgroundColor?: string;
      backgroundImage?: string;
    };
    formSettings: {
      successMessage?: string;
      loadingMessage?: string;
      agentSessionMode?: boolean;
    };
  };
  isInitialized: boolean;
  varInputNodes: {
    id: string;
    label: string;
    type: string;
    flowName: string;
    flowId: string;
  }[];
  varOutputNodes: {
    id: string;
    label: string;
    type: string;
    flowName: string;
    flowId: string;
  }[];
}

export enum ActionTypes {
  UPDATE_FORM = 'UPDATE_FORM',
  ADD_NODES = 'ADD_NODES',
  REMOVE_NODES = 'REMOVE_NODES',
  UPDATE_NODES_LAYOUT = 'UPDATE_NODES_LAYOUT',
  UPDATE_NODES_DATA = 'UPDATE_NODES_DATA',
  UPDATE_FORM_STATUS = 'UPDATE_FORM_STATUS',
  UPDATE_PUBLISH = 'UPDATE_PUBLISH',
  UPDATE_ACTIVE_FLOWS = 'UPDATE_ACTIVE_FLOWS',
  UPDATE_FORM_STYLES = 'UPDATE_FORM_STYLES',
  UPDATE_FORM_SETTINGS = 'UPDATE_FORM_SETTINGS',
  DUPLICATE_NODE = 'DUPLICATE_NODE',
}

export type IActionType =
  | {
      type: ActionTypes.UPDATE_FORM;
      payload: {
        form: FormBuilderState['form'];
        varInputNodes: FormBuilderState['varInputNodes'];
        varOutputNodes: FormBuilderState['varOutputNodes'];
      };
    }
  | {
      type: ActionTypes.ADD_NODES;
      payload: {
        type: ITypeNode;
      };
    }
  | { type: ActionTypes.REMOVE_NODES; payload: string }
  | {
      type: ActionTypes.UPDATE_NODES_LAYOUT;
      payload: { id: string; updates: { layout: INode['layout'] } };
    }
  | {
      type: ActionTypes.UPDATE_NODES_DATA;
      payload: { id: string; updates: { data: INode['data'] } };
    }
  | { type: ActionTypes.UPDATE_FORM_STATUS; payload: 'draft' | 'published' }
  | {
      type: ActionTypes.UPDATE_PUBLISH;
      payload: {
        status: 'published' | 'draft';
        publishType: 'internal' | 'external';
        publishedNodes: any;
      };
    }
  | {
      type: ActionTypes.UPDATE_ACTIVE_FLOWS;
      payload: {
        activeFlows: string[];
        varInputNodes: FormBuilderState['varInputNodes'];
        varOutputNodes: FormBuilderState['varOutputNodes'];
      };
    }
  | {
      type: ActionTypes.UPDATE_FORM_STYLES;
      payload: { backgroundColor?: string; backgroundImage?: string };
    }
  | {
      type: ActionTypes.UPDATE_FORM_SETTINGS;
      payload: { successMessage?: string; loadingMessage?: string; agentSessionMode?: boolean };
    }
  | {
      type: ActionTypes.DUPLICATE_NODE;
      payload: { node: INode };
    };

export function formBuilderReducer(
  state: FormBuilderState,
  action: IActionType,
): FormBuilderState {
  switch (action.type) {
    case ActionTypes.UPDATE_FORM:
      return {
        ...state,
        form: action.payload.form,
        varInputNodes: action.payload.varInputNodes,
        varOutputNodes: action.payload.varOutputNodes,
      };

    case ActionTypes.ADD_NODES:
      const id = `${action.payload.type}-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 9)}`;

      const node: INode = {
        id,
        type: action.payload.type,
        data: {} as INode['data'],
        layout: {
          i: id,
          x: 0,
          y: Math.max(
            ...state.form.newFormNodes.map(
              item => item.layout.y + item.layout.h,
            ),
            0,
          ),
          w: 0,
          h: 0,
          minW: 0,
          minH: 0,
        },
      };

      if (action.payload.type === ITypeNode.TITLE) {
        node.data = {
          title: 'AI-Powered Form Builder',
          styles: {
            title: {
              fontFamily: 'Roboto, sans-serif',
              fontSize: 20,
              isBold: true,
              isItalic: false,
              isUnderlined: false,
              textAlign: 'left',
              color: '#101828',
            },
            backgroundColor: '#ffffff',
            borderRadius: 16,
            borderColor: '#EAECF0',
            borderWeight: 1,
            verticalAlign: 'center',
            padding: 16,
          },
        };

        node.layout.w = 12;
        node.layout.h = 2;
        node.layout.minW = 3;
        node.layout.minH = 2;
      }

      if (action.payload.type === ITypeNode.SHORT_TEXT) {
        node.data = {
          text: 'AI helps you build smarter forms. It adapts to your needs instantly. Save time with intelligent automation',
          styles: {
            shortText: {
              fontFamily: 'Roboto, sans-serif',
              fontSize: 14,
              isBold: false,
              isItalic: false,
              isUnderlined: false,
              textAlign: 'left',
              color: '#475467',
            },
            backgroundColor: '#ffffff',
            borderRadius: 16,
            borderColor: '#EAECF0',
            borderWeight: 1,
            verticalAlign: 'center',
            padding: 16,
          },
        };

        node.layout.w = 12;
        node.layout.h = 2;
        node.layout.minW = 3;
        node.layout.minH = 2;
      }

      if (action.payload.type === ITypeNode.IMAGE) {
        node.data = {
          url: '',
          styles: {
            backgroundColor: '#ffffff',
            borderRadius: 16,
            borderColor: '#EAECF0',
            borderWeight: 1,
            verticalAlign: 'center',
            padding: 16,
          },
        };

        node.layout.w = 12;
        node.layout.h = 4;
        node.layout.minW = 3;
        node.layout.minH = 4;
      }

      if (action.payload.type === ITypeNode.INPUT) {
        node.data = {
          varDataInputId: '',
          fieldType: 'data',
          requiredField: false,
          allowedFiles: [],
          minRows: 3,
          title: 'Project Name',
          subtitle: 'Enter the name of your AI project',
          styles: {
            title: {
              fontFamily: 'Roboto, sans-serif',
              fontSize: 20,
              isBold: true,
              isItalic: false,
              isUnderlined: false,
              textAlign: 'left',
              color: '#101828',
            },
            subtitle: {
              fontFamily: 'Roboto, sans-serif',
              fontSize: 14,
              isBold: false,
              isItalic: false,
              isUnderlined: false,
              textAlign: 'left',
              color: '#475467',
            },
            verticalAlign: 'center',
            backgroundColor: '#ffffff',
            borderRadius: 16,
            borderColor: '#EAECF0',
            borderWeight: 1,
            padding: 16,
          },
        };

        node.layout.w = 12;
        node.layout.h = 6;
        node.layout.minW = 3;
        node.layout.minH = 6;
      }

      if (action.payload.type === ITypeNode.OUTPUT) {
        node.data = {
          varDataInputId: '',
          fieldType: 'standard',
          title: 'AI Generated Result',
          subtitle: 'Here is the output based on your input',
          styles: {
            title: {
              fontFamily: 'Roboto, sans-serif',
              fontSize: 20,
              isBold: true,
              isItalic: false,
              isUnderlined: false,
              textAlign: 'left',
              color: '#101828',
            },
            subtitle: {
              fontFamily: 'Roboto, sans-serif',
              fontSize: 14,
              isBold: false,
              isItalic: false,
              isUnderlined: false,
              textAlign: 'left',
              color: '#475467',
            },
            backgroundColor: '#ffffff',
            borderRadius: 16,
            borderColor: '#EAECF0',
            borderWeight: 1,
            verticalAlign: 'center',
            padding: 16,
          },
        };

        node.layout.w = 12;
        node.layout.h = 5;
        node.layout.minW = 3;
        node.layout.minH = 5;
      }

      if (action.payload.type === ITypeNode.LIST) {
        node.data = {
          title: 'AI Use Cases',
          listItems: [
            {
              description: 'AI-powered form builder',
            },
            {
              description: 'AI-powered chatbot',
            },
          ],
          question: 'Where would you like to apply AI?',
          typeQuestion: 'single',
          varDataInputId: '',
          requiredField: false,
          styles: {
            title: {
              fontFamily: 'Roboto, sans-serif',
              fontSize: 20,
              isBold: true,
              isItalic: false,
              isUnderlined: false,
              textAlign: 'left',
              color: '#101828',
            },
            question: {
              fontFamily: 'Roboto, sans-serif',
              fontSize: 14,
              isBold: false,
              isItalic: false,
              isUnderlined: false,
              textAlign: 'left',
              color: '#475467',
            },
            backgroundColor: '#ffffff',
            borderRadius: 16,
            borderColor: '#EAECF0',
            borderWeight: 1,
            verticalAlign: 'center',
            padding: 16,
          },
        };

        node.layout.w = 12;
        node.layout.h = 4;
        node.layout.minW = 3;
        node.layout.minH = 4;
      }

      if (action.payload.type === ITypeNode.SUBMIT) {
        node.data = {
          removeOrRetain: true,
          styles: {
            submit: {
              fontFamily: 'Roboto, sans-serif',
              fontSize: 16,
              isBold: false,
              isItalic: false,
              isUnderlined: false,
              textAlign: 'center',
              color: '#ffffff',
              buttonBackgroundColor: '#3650DE',
            },
            backgroundColor: '#ffffff',
            borderRadius: 14,
            borderColor: '#EAECF0',
            borderWeight: 1,
            verticalAlign: 'center',
            padding: 16,
          },
        };

        node.layout.w = 12;
        node.layout.h = 2;
        node.layout.minW = 2;
        node.layout.minH = 2;
      }

      return {
        ...state,
        form: {
          ...state.form,
          newFormNodes: [...state.form.newFormNodes, node],
          updatedAt: new Date().toISOString(),
        },
        isInitialized: true,
      };

    case ActionTypes.REMOVE_NODES:
      return {
        ...state,
        form: {
          ...state.form,
          newFormNodes: state.form.newFormNodes.filter(
            item => item.id !== action.payload,
          ),
        },
        isInitialized: true,
      };

    case ActionTypes.UPDATE_NODES_LAYOUT:
      return {
        ...state,
        form: {
          ...state.form,
          newFormNodes: state.form.newFormNodes.map(item =>
            item.id === action.payload.id
              ? { ...item, layout: action.payload.updates.layout }
              : item,
          ),
          updatedAt: new Date().toISOString(),
        },
        isInitialized: true,
      };

    case ActionTypes.UPDATE_FORM_STATUS:
      return {
        ...state,
        form: {
          ...state.form,
          status: action.payload,
          updatedAt: new Date().toISOString(),
        },
        isInitialized: true,
      };

    case ActionTypes.UPDATE_ACTIVE_FLOWS:
      return {
        ...state,
        form: {
          ...state.form,
          activeFlows: action.payload.activeFlows,
          updatedAt: new Date().toISOString(),
        },
        isInitialized: true,
        varInputNodes: action.payload.varInputNodes,
        varOutputNodes: action.payload.varOutputNodes,
      };

    case ActionTypes.UPDATE_PUBLISH:
      return {
        ...state,
        form: {
          ...state.form,
          status: action.payload.status,
          publishType: action.payload.publishType,
          newFormNodesPublished: action.payload.publishedNodes,
          updatedAt: new Date().toISOString(),
        },
        isInitialized: true,
      };

    case ActionTypes.UPDATE_NODES_DATA:
      return {
        ...state,
        form: {
          ...state.form,
          newFormNodes: state.form.newFormNodes.map(item =>
            item.id === action.payload.id
              ? { ...item, data: action.payload.updates.data }
              : item,
          ),
          updatedAt: new Date().toISOString(),
        },
        isInitialized: true,
      };

    case ActionTypes.UPDATE_FORM_STYLES:
      return {
        ...state,
        form: {
          ...state.form,
          formStyle: action.payload,
        },
        isInitialized: true,
      };

    case ActionTypes.UPDATE_FORM_SETTINGS:
      return {
        ...state,
        form: {
          ...state.form,
          formSettings: action.payload,
        },
        isInitialized: true,
      };

    case ActionTypes.DUPLICATE_NODE:
      const originalNode = action.payload.node;
      const newId = `${originalNode.type}-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 9)}`;

      const duplicatedNode: INode = {
        ...originalNode,
        id: newId,
        layout: {
          ...originalNode.layout,
          i: newId,
          x: originalNode.layout.x,
          y: Math.max(
            ...state.form.newFormNodes.map(
              item => item.layout.y + item.layout.h,
            ),
            0,
          ),
        },
      };

      return {
        ...state,
        form: {
          ...state.form,
          newFormNodes: [...state.form.newFormNodes, duplicatedNode],
          updatedAt: new Date().toISOString(),
        },
        isInitialized: true,
      };

    default:
      return state;
  }
}
